import { useCallback, useEffect, useRef, useState } from 'react';

const eligible = slab => slab?.grader === 'ACE' && /^\d{5,8}$/.test(slab.cert || '');
const active = job => ['queued', 'running'].includes(job?.status);
function discordLink(value) {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname === 'discord.com'
      && /^\/channels\/\d+\/\d+\/\d+$/.test(url.pathname)
      && !url.search && !url.hash && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}

// One controller serves the selected slab panel and its existing details sheet.
// Operations capture their certificate, so browsing never retargets a request.
export function useSlabReveal(slab, request) {
  const [data, setData] = useState(null);
  const [readError, setReadError] = useState('');
  const [actions, setActions] = useState({});
  const [review, setReview] = useState(null);
  const mounted = useRef(false), sequence = useRef(0), lifetime = useRef(0), locks = useRef(new Map());
  const refresh = useCallback(async () => {
    if (!request) return;
    const revision = ++sequence.current;
    try {
      const result = await request('/api/reveals');
      if (!mounted.current || revision !== sequence.current) return;
      if (!Array.isArray(result?.jobs) || !result.worker) throw Error('Could not verify reveal status.');
      setData(result); setReadError('');
      setActions(previous => Object.fromEntries(Object.entries(previous).map(([key, value]) => [key, { ...value, error: '' }])));
    } catch (error) {
      if (mounted.current && revision === sequence.current) setReadError(error.message || 'Could not load reveal status.');
    }
  }, [request]);
  useEffect(() => {
    mounted.current = true; lifetime.current++; locks.current.clear(); setData(null); setActions({}); setReadError(''); setReview(null);
    if (request) refresh();
    const timer = request ? setInterval(refresh, 10000) : null;
    return () => { mounted.current = false; lifetime.current++; sequence.current++; clearInterval(timer); };
  }, [request, refresh]);

  const cert = eligible(slab) ? slab.cert : null;
  const action = actions[cert] || {};
  const jobs = (data?.jobs || []).filter(job => job.slabs?.some(item => item.cert === cert));
  const receipt = action.receipt && data?.jobs.find(job => job.id === action.receipt);
  const job = jobs.find(active) || (action.receipt && !receipt ? { id: action.receipt, status: 'queued' } : jobs[0]);
  async function submit(retryId) {
    if (!cert || !request || locks.current.has(cert) || active(job)) return;
    const operation = Symbol(cert), generation = lifetime.current;
    locks.current.set(cert, operation);
    setActions(previous => ({ ...previous, [cert]: { ...previous[cert], busy: true, error: '' } }));
    try {
      const result = retryId
        ? await request(`/api/reveals/${encodeURIComponent(retryId)}/retry`, { checked: true })
        : await request('/api/reveals', { certs: [cert], drop: false });
      if (!result?.id) throw Error('Could not verify the queue receipt. Try again to check.');
      if (mounted.current && generation === lifetime.current) {
        setActions(previous => ({ ...previous, [cert]: { receipt: result.id, busy: false } }));
        setReview(null); await refresh();
      }
    } catch (error) {
      if (mounted.current && generation === lifetime.current) setActions(previous => ({ ...previous, [cert]: { ...previous[cert], busy: false, error: error.message || 'Could not queue this reveal.' } }));
    } finally { if (locks.current.get(cert) === operation) locks.current.delete(cert); }
  }
  if (!request || !cert) return null;
  return {
    cert, job, busy: !!action.busy, loading: !data, error: action.error || readError,
    online: data?.worker.online, reviewing: review === job?.id,
    queue: () => submit(), retry: () => submit(job.id),
    review: () => setReview(job.id), cancel: () => setReview(null), refresh,
  };
}

export default function SlabRevealAction({ control }) {
  if (!control) return null;
  const { job, busy, loading, error, online, reviewing } = control;
  const running = active(job), failed = job?.status === 'failed';
  const link = job?.status === 'done' ? discordLink(job.result?.discord_url) : null;
  return <section className="sv-reveal-action" aria-label={`Reveal for cert ${control.cert}`}>
    {!failed && <button type="button" className="sv-pill" disabled={busy || loading || running || !!error} onClick={control.queue}>
      {busy ? 'Queueing reveal…' : running ? (job.status === 'running' ? 'Making reveal…' : 'Reveal queued') : job?.status === 'done' ? 'Make another reveal' : 'Make reveal'}
    </button>}
    {loading && <p role="status">Checking reveal status…</p>}
    {!loading && online === false && <p role="status">Mac mini offline. Your reveal will wait until it’s back.</p>}
    {running && <p role="status">{job.status === 'queued' ? 'Queued for this slab.' : 'Making this slab’s film and still…'}</p>}
    {job?.status === 'done' && <p role="status">Reveal ready. {link ? <a href={link} target="_blank" rel="noopener noreferrer">Open in Discord</a> : 'Check the Discord channel for the film and still.'}</p>}
    {failed && <>
      <p role="alert">{job.message || 'This reveal failed.'}</p>
      {job.retry_allowed !== false && !reviewing && <button type="button" className="sv-pill" disabled={busy || loading} onClick={control.review}>Retry reveal</button>}
      {reviewing && <div className="sv-reveal-retry"><p>Check Discord and confirm the previous job has stopped before retrying, to avoid posting twice.</p>
        <button type="button" className="sv-pill" disabled={busy} onClick={control.retry}>I checked; retry</button>
        <button type="button" className="sv-pill" disabled={busy} onClick={control.cancel}>Cancel</button>
      </div>}
    </>}
    {!!job?.result?.missing?.length && <p role="alert">No ACE scans for {job.result.missing.join(', ')}.</p>}
    {error && <><p role="alert">{error}</p><button type="button" className="sv-pill" disabled={busy} onClick={control.refresh}>Check reveal status</button></>}
  </section>;
}
